import axios from "axios";
import { tokenManager } from "utils/tokenManager";

const KPI_API_BASE = "https://usibras-web-backend.onrender.com/api";

/**
 * Fetch all KPI users (workers) from the backend.
 * Uses Bearer token from tokenManager for authorization.
 *
 * @returns {Promise<{ data: Array, message: string }>}
 */
export async function getKpiUsers() {
  const authHeader = tokenManager.getAuthHeader();
  const headers = {};
  if (authHeader) {
    headers.Authorization = authHeader;
  }
  const response = await axios.get(`${KPI_API_BASE}/kpi-users`, { headers });
  return response.data;
}

/**
 * Fetch KPI users (workers) by sector and role.
 *
 * @param {string} sectorName - Sector name (e.g. "Warehouse", "Raw Nut Receiving")
 * @param {string} role - "operator" | "supervisor"
 * @returns {Promise<{ data: Array, message: string }>}
 */
export async function getKpiUsersBySectorAndRole(sectorName, role) {
  const authHeader = tokenManager.getAuthHeader();
  const headers = {};
  if (authHeader) {
    headers.Authorization = authHeader;
  }
  const params = new URLSearchParams();
  if (sectorName) params.set("sector_name", sectorName);
  if (role) params.set("role", role);
  const response = await axios.get(
    `${KPI_API_BASE}/kpi-users?${params.toString()}`,
    { headers }
  );
  return response.data;
}

/**
 * Register a new KPI user (worker) with the backend.
 * A password is sent to their email.
 * Uses Bearer token from tokenManager for authorization.
 *
 * @param {Object} data
 * @param {string} data.name - Full name
 * @param {string} data.email - Work email (required; password sent here)
 * @param {string} data.role - "admin" | "supervisor" | "operator"
 * @param {string} [data.employee_id] - Employee ID
 * @param {string} [data.sector_name] - Sector name (for supervisor/operator)
 * @returns {Promise<{ user: Object, message: string, email_sent: boolean }>}
 */
export async function registerKpiUser(data) {
  const authHeader = tokenManager.getAuthHeader();
  const headers = {};
  if (authHeader) {
    headers.Authorization = authHeader;
  }
  const response = await axios.post(
    `${KPI_API_BASE}/kpi-users/register`,
    {
      name: data.name?.trim() || "",
      email: data.email?.trim() || "",
      role: (data.role || "operator").toLowerCase(),
      employee_id: data.employee_id?.trim() || "",
      sector_name: data.sector_name?.trim() || "",
    },
    { headers }
  );
  return response.data;
}
